import { useState, useCallback, useRef } from 'react';
import { useMutation, gql } from '@apollo/client';

// Media CDN specific mutations
const MEDIA_CDN_INITIATE_UPLOAD = gql`
  mutation MediaCDNInitiateUpload($fileName: String!, $totalChunks: Float!, $fileSize: Float!) {
    mediaCDNInitiateUpload(fileName: $fileName, totalChunks: $totalChunks, fileSize: $fileSize)
  }
`;

const MEDIA_CDN_UPLOAD_CHUNK = gql`
  mutation MediaCDNUploadChunk($uploadId: String!, $chunkNumber: Float!, $chunk: Upload!) {
    mediaCDNUploadChunk(uploadId: $uploadId, chunkNumber: $chunkNumber, chunk: $chunk)
  }
`;

const MEDIA_CDN_COMPLETE_UPLOAD = gql`
  mutation MediaCDNCompleteUpload($uploadId: String!) {
    mediaCDNCompleteUpload(uploadId: $uploadId) {
      ipfsHash
      url
      fileSize
      mimeType
      fileName
    }
  }
`;

const MEDIA_CDN_CANCEL_UPLOAD = gql`
  mutation MediaCDNCancelUpload($uploadId: String!) {
    mediaCDNCancelUpload(uploadId: $uploadId)
  }
`;

const MEDIA_CDN_GET_PROGRESS = gql`
  mutation MediaCDNGetUploadProgress($uploadId: String!) {
    mediaCDNGetUploadProgress(uploadId: $uploadId)
  }
`;

export interface MediaCDNUploadResult {
  ipfsHash: string;
  url: string;
  fileSize: number;
  mimeType: string;
  fileName: string;
}

export interface ChunkedUploadProgress {
  uploadedChunks: number;
  totalChunks: number;
  percentComplete: number;
  currentChunk: number;
  uploadSpeed: string;
  estimatedTimeRemaining: string;
  uploadPhase?: 'uploading_chunks' | 'chunks_complete' | 'transferring_to_ipfs' | 'ipfs_processing' | 'complete' | 'failed';
  phase?: 'uploading_chunks' | 'chunks_complete' | 'transferring_to_ipfs' | 'ipfs_processing' | 'complete' | 'failed';
  chunksProgress?: number;
  transferProgress?: number;
  ipfsProcessingProgress?: number;
  ipfsUploadedBytes?: number;
  ipfsTotalBytes?: number;
  chunksElapsedSeconds?: number;
  chunksSpeedMBps?: number;
  chunksETASeconds?: number;
  transferElapsedSeconds?: number;
  transferSpeedMBps?: number;
  transferETASeconds?: number;
  processingElapsedSeconds?: number;
  processingSpeedMBps?: number;
  processingETASeconds?: number;
  ipfsUploadStatus?: 'pending' | 'uploading' | 'complete' | 'failed';
  ipfsUploadProgress?: number;
}

export interface UseChunkedUploadOptions {
  chunkSize?: number;
  onProgress?: (progress: ChunkedUploadProgress) => void;
  onComplete?: (result: MediaCDNUploadResult) => void;
  onError?: (error: Error) => void;
}

export const useChunkedUpload = (options: UseChunkedUploadOptions = {}) => {
  const {
    chunkSize = 10 * 1024 * 1024, // 10MB default
    onProgress,
    onComplete,
    onError
  } = options;

  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState<ChunkedUploadProgress>({
    uploadedChunks: 0,
    totalChunks: 0,
    percentComplete: 0,
    currentChunk: 0,
    uploadSpeed: '0',
    estimatedTimeRemaining: 'Calculating...'
  });

  const abortControllerRef = useRef<AbortController | null>(null);
  const uploadStartTimeRef = useRef<number>(0);
  const uploadedBytesRef = useRef<number>(0);
  const fileSizeRef = useRef<number>(0);

  const [initiateUpload] = useMutation(MEDIA_CDN_INITIATE_UPLOAD);
  const [uploadChunkMutation] = useMutation(MEDIA_CDN_UPLOAD_CHUNK);
  const [completeUpload] = useMutation(MEDIA_CDN_COMPLETE_UPLOAD);
  const [cancelUploadMutation] = useMutation(MEDIA_CDN_CANCEL_UPLOAD);
  const [getProgressMutation] = useMutation(MEDIA_CDN_GET_PROGRESS);

  const calculateSpeed = (bytesUploaded: number, timeElapsed: number): string => {
    const mbUploaded = bytesUploaded / (1024 * 1024);
    const secondsElapsed = timeElapsed / 1000;
    const speed = mbUploaded / secondsElapsed;
    return speed.toFixed(2);
  };

  const calculateOverallSpeed = (percentComplete: number, timeElapsedMs: number): string => {
    if (percentComplete <= 0 || fileSizeRef.current === 0) {
      return '0.00';
    }
    const processedBytes = (fileSizeRef.current * percentComplete) / 100;
    const secondsElapsed = timeElapsedMs / 1000;
    const mbProcessed = processedBytes / (1024 * 1024);
    const speed = mbProcessed / secondsElapsed;
    return speed.toFixed(2);
  };

  const calculateETA = (bytesRemaining: number, speed: number): string => {
    if (speed === 0) return 'Calculating...';
    const secondsRemaining = bytesRemaining / (speed * 1024 * 1024);
    if (secondsRemaining < 60) {
      return `${Math.round(secondsRemaining)}s`;
    } else if (secondsRemaining < 3600) {
      return `${Math.round(secondsRemaining / 60)}m`;
    } else {
      const hours = Math.floor(secondsRemaining / 3600);
      const minutes = Math.round((secondsRemaining % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  };

  const calculateOverallETA = (percentComplete: number, timeElapsedMs: number): string => {
    if (percentComplete <= 0 || percentComplete >= 100) {
      return 'Calculating...';
    }
    const totalEstimatedMs = (timeElapsedMs / percentComplete) * 100;
    const remainingMs = totalEstimatedMs - timeElapsedMs;
    const secondsRemaining = remainingMs / 1000;
    if (secondsRemaining < 60) {
      return `${Math.round(secondsRemaining)}s`;
    } else if (secondsRemaining < 3600) {
      return `${Math.round(secondsRemaining / 60)}m`;
    } else {
      const hours = Math.floor(secondsRemaining / 3600);
      const minutes = Math.round((secondsRemaining % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  };

  const uploadFile = useCallback(async (file: File): Promise<MediaCDNUploadResult> => {
    if (isUploading) {
      throw new Error('Upload already in progress');
    }

    setIsUploading(true);
    abortControllerRef.current = new AbortController();
    uploadStartTimeRef.current = Date.now();
    uploadedBytesRef.current = 0;
    fileSizeRef.current = file.size;

    try {
      const totalChunks = Math.ceil(file.size / chunkSize);
      console.log(`📦 [MediaCDN] Starting upload: ${file.name}`);
      console.log(`📊 File size: ${(file.size / 1024 / 1024).toFixed(2)}MB, Chunks: ${totalChunks}`);

      setProgress({
        uploadedChunks: 0,
        totalChunks,
        percentComplete: 0,
        currentChunk: 0,
        uploadSpeed: '0',
        estimatedTimeRemaining: 'Calculating...',
        phase: 'uploading_chunks',
        chunksProgress: 0,
        transferProgress: 0,
        ipfsProcessingProgress: 0
      });

      // Step 1: Initiate upload
      const { data: initiateData } = await initiateUpload({
        variables: {
          fileName: file.name,
          totalChunks,
          fileSize: file.size
        }
      });

      const uploadId = initiateData.mediaCDNInitiateUpload;
      console.log(`✅ [MediaCDN] Upload session: ${uploadId}`);

      // Step 2: Upload chunks
      for (let chunkNumber = 0; chunkNumber < totalChunks; chunkNumber++) {
        if (abortControllerRef.current?.signal.aborted) {
          await cancelUploadMutation({ variables: { uploadId } });
          throw new Error('Upload cancelled');
        }

        const start = chunkNumber * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);

        await uploadChunkMutation({
          variables: { uploadId, chunkNumber, chunk }
        });

        uploadedBytesRef.current += chunk.size;
        const timeElapsed = Date.now() - uploadStartTimeRef.current;
        const speed = parseFloat(calculateSpeed(uploadedBytesRef.current, timeElapsed));
        const bytesRemaining = file.size - uploadedBytesRef.current;
        const eta = calculateETA(bytesRemaining, speed);

        // Calculate ETA in seconds for the phase display
        const etaSeconds = speed > 0 ? bytesRemaining / (speed * 1024 * 1024) : undefined;

        const newProgress: ChunkedUploadProgress = {
          uploadedChunks: chunkNumber + 1,
          totalChunks,
          percentComplete: Math.round(((chunkNumber + 1) / totalChunks) * 100),
          currentChunk: chunkNumber + 1,
          uploadSpeed: calculateSpeed(uploadedBytesRef.current, timeElapsed),
          estimatedTimeRemaining: eta,
          phase: 'uploading_chunks',
          chunksProgress: Math.round(((chunkNumber + 1) / totalChunks) * 100),
          transferProgress: 0,
          ipfsProcessingProgress: 0,
          // Set phase-specific speed and ETA for the UI
          chunksSpeedMBps: speed,
          chunksETASeconds: etaSeconds,
          chunksElapsedSeconds: timeElapsed / 1000
        };

        setProgress(newProgress);
        onProgress?.(newProgress);
      }

      // Step 3: Complete upload with polling
      console.log('🏁 [MediaCDN] All chunks uploaded, completing...');

      const completePromise = completeUpload({ variables: { uploadId } });

      const pollInterval = setInterval(async () => {
        try {
          const { data: progressData } = await getProgressMutation({
            variables: { uploadId }
          });

          if (progressData?.mediaCDNGetUploadProgress) {
            const serverProgress = JSON.parse(progressData.mediaCDNGetUploadProgress);
            const timeElapsed = Date.now() - uploadStartTimeRef.current;

            const updatedProgress: ChunkedUploadProgress = {
              uploadedChunks: serverProgress.uploadedChunks,
              totalChunks: serverProgress.totalChunks,
              percentComplete: serverProgress.percentComplete,
              currentChunk: serverProgress.uploadedChunks,
              uploadSpeed: calculateOverallSpeed(serverProgress.percentComplete, timeElapsed),
              estimatedTimeRemaining: calculateOverallETA(serverProgress.percentComplete, timeElapsed),
              uploadPhase: serverProgress.uploadPhase,
              phase: serverProgress.phase,
              chunksProgress: serverProgress.chunksProgress,
              transferProgress: serverProgress.transferProgress,
              ipfsProcessingProgress: serverProgress.ipfsProcessingProgress,
              ipfsUploadedBytes: serverProgress.ipfsUploadedBytes,
              ipfsTotalBytes: serverProgress.ipfsTotalBytes,
              chunksElapsedSeconds: serverProgress.chunksElapsedSeconds,
              chunksSpeedMBps: serverProgress.chunksSpeedMBps,
              chunksETASeconds: serverProgress.chunksETASeconds,
              transferElapsedSeconds: serverProgress.transferElapsedSeconds,
              transferSpeedMBps: serverProgress.transferSpeedMBps,
              transferETASeconds: serverProgress.transferETASeconds,
              processingElapsedSeconds: serverProgress.processingElapsedSeconds,
              processingSpeedMBps: serverProgress.processingSpeedMBps,
              processingETASeconds: serverProgress.processingETASeconds,
              ipfsUploadStatus: serverProgress.ipfsUploadStatus,
              ipfsUploadProgress: serverProgress.ipfsUploadProgress
            };

            setProgress(updatedProgress);
            onProgress?.(updatedProgress);
          }
        } catch (error) {
          console.error('[MediaCDN] Progress poll error:', error);
        }
      }, 1000);

      const { data: completeData } = await completePromise;
      clearInterval(pollInterval);

      const result: MediaCDNUploadResult = completeData.mediaCDNCompleteUpload;
      console.log(`🎉 [MediaCDN] Upload complete! Hash: ${result.ipfsHash}`);
      console.log(`🔗 URL: ${result.url}`);

      setProgress(prev => ({ ...prev, percentComplete: 100, phase: 'complete' }));
      onComplete?.(result);

      return result;
    } catch (error: any) {
      console.error('[MediaCDN] Upload error:', error);
      onError?.(error);
      throw error;
    } finally {
      setIsUploading(false);
      abortControllerRef.current = null;
    }
  }, [
    isUploading,
    chunkSize,
    initiateUpload,
    uploadChunkMutation,
    completeUpload,
    cancelUploadMutation,
    getProgressMutation,
    onProgress,
    onComplete,
    onError
  ]);

  const cancelCurrentUpload = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  return {
    uploadFile,
    isUploading,
    progress,
    cancelUpload: cancelCurrentUpload
  };
};
